import { useEffect, useState } from "react";
import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import CreateRecordButton from "../input/CreateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import useNotification from "../../hooks/useNotification";

/**
 * @COMPONENT_NAME CardType
 * @DESCRIPTION CardType을 관리하는 화면 컴포넌트
 * @PASSTHROUGH_PROPS
 *   - {Object} schema - CardType 테이블 스키마 정보
 *   - {Object} grid - CardType 그리드 객체 참조
 *   - {Object} selectedItem - 선택된 CardType 정보
 *   - {Object} api - CardType API 객체
 * @CALLBACKS
 *   - {Function} onSuccess - 작업 성공 시 호출되는 콜백
 *   - {Function} onFailure - 작업 실패 시 호출되는 콜백
 *   - {Function} onNotifyWarning - 경고 발생 시 호출되는 콜백
 */
const CardType = () => {
    const table = "CardType";
    const view = "VwCardType";

    const [schema, setSchema] = useState();
    const [grid, setGrid] = useState();
    const [selectedItem, setSelectedItem] = useState();
    const [api, setApi] = useState();

    const { notifySuccess, notifyFailure, notifyWarning } = useNotification();

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid) => {
                setSelectedItem(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            grid.selectionChanged.addHandler(handleGridSelection);
            grid.itemsSourceChanged.addHandler(handleGridSelection);

            return () => {
                if (grid && !grid.isDestroyed) {
                    grid.selectionChanged.removeHandler(handleGridSelection);
                    grid.itemsSourceChanged.removeHandler(handleGridSelection);
                }
            };
        }
    }, [grid]);

    const onSuccess = (msg, position) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <>
            <BasicCrudScreenType
                id={table}
                table={table}
                view={view}
                onInitialized={(grid) => setGrid(grid)}
                onApiInitialized={(api) => setApi(api)}
                noCreate
                noEdit
                noDelete
                noExcel
                onAppendButtonInFrontOfSearch={() => (
                    <>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <CreateRecordButton
                                    id={table}
                                    schema={schema}
                                    refresh={api}
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    modifyFormDataCallback={(newFormData, name, value) => {
                                        if (name === "coreSize") {
                                            newFormData["pcsPerSheet"] = value;
                                        }
                                    }}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="PATCH">
                                <UpdateRecordButton
                                    id={table}
                                    schema={schema}
                                    grid={grid}
                                    refresh={api}
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    modifyFormDataCallback={(newFormData, name, value) => {
                                        if (name === "coreSize") {
                                            newFormData["pcsPerSheet"] = value;
                                        }
                                    }}
                                    disabled={selectedItem?.length === 0}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <DeleteRecordButton
                                    id={table}
                                    schema={schema}
                                    refresh={api}
                                    grid={grid}
                                    color="secondary"
                                    disabled={selectedItem?.length === 0}
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    onPartialFailure={onNotifyWarning}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                    </>
                )}
            />
        </>
    );
};

export default CardType;
